import { useState, useRef, useEffect } from "react";
import { FileUploader } from "react-drag-drop-files";
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from "react-image-crop";
import "react-image-crop/src/ReactCrop.scss";
import { useMutation } from "@tanstack/react-query";
import { postProfileImage } from "../../../js/api/my-style/post-profile-image";
import { useDebounceEffect } from "./lib/use-debounce-effect";
import { canvasPreview } from "./lib/canvas-preview";
import { SharedIcon } from "../shared/shared-icon";

const ASPECT_CROP = 1;

const UploaderInner = () => {
  return (
    <div className="profile-image__uploader-inner">
      <p>
        <span className="profile-image__uploader-inner--drag">
          Drag photos here or{" "}
        </span>
        <span className="profile-image__uploader-inner--browse">
          browse files
        </span>
      </p>

      <p>
        PNG, JPG or JPEG.
        <br />
        File must not exceed 5Mb.
      </p>
    </div>
  );
};

export const ProfileImageModal = ({ closeModal }) => {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [avatar, setAvatar] = useState(null);
  const [crop, setCrop] = useState();
  const [file, setFile] = useState({
    name: "",
    type: "",
  });
  const [uploadError, setUploadError] = useState({
    sizeError: false,
    typeError: false,
  });
  const updateAvatar = useMutation({
    mutationFn: postProfileImage,
    onSuccess: (data) => {
      window.location.reload();
    },
  });

  useDebounceEffect(
    async () => {
      if (crop?.width && crop?.height && imgRef.current && canvasRef.current) {
        canvasPreview(imgRef.current, canvasRef.current, crop);
      }
    },
    100,
    [crop]
  );

  function onSelectFile(file) {
    if (file) {
      setIsDisabled(false);
      setUploadError({ sizeError: false, typeError: false });
      setFile({
        name: file.name,
        type: file.type,
      });
      const reader = new FileReader();
      reader.addEventListener("load", () =>
        setAvatar(reader.result?.toString() || "")
      );
      reader.readAsDataURL(file);
    }
  }

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;

    setCrop(
      centerCrop(
        convertToPixelCrop(
          makeAspectCrop(
            {
              unit: "%",
              height: 80,
            },
            ASPECT_CROP,
            width,
            height
          ),
          width,
          height
        ),
        width,
        height
      )
    );
  }

  const uploadHandler = async () => {
    const image = imgRef.current;
    const canvas = canvasRef.current;

    if (image || canvas || crop) {
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      const offscreen = new OffscreenCanvas(
        crop.width * scaleX,
        crop.height * scaleY
      );
      const context = offscreen.getContext("2d");
      context.drawImage(
        canvas,
        0,
        0,
        canvas.width,
        canvas.height,
        0,
        0,
        offscreen.width,
        offscreen.height
      );

      const blob = await offscreen.convertToBlob({
        type: file.type,
      });

      const formData = new FormData();
      formData.append("ProfileImage", blob, file.name);

      updateAvatar.mutate(formData);
    }
  };

  if (updateAvatar.isPending || updateAvatar.isSuccess) {
    return (
      <div className="saved-colors-loading-container">
        <SharedIcon
          iconName="#sprite-spinner"
          className="animate-spin"
        />
      </div>
    );
  }

  return (
    <>
      <div className="profile-image__header">
        <h4 className="profile-image__title">Upload a photo</h4>
      </div>
      <div className="profile-image__main">
        {!avatar && (
          <FileUploader
            name="file"
            classes={"profile-image__uploader"}
            handleChange={onSelectFile}
            children={<UploaderInner />}
            types={["JPG", "PNG", "JPEG"]}
            onTypeError={() =>
              setUploadError({ sizeError: false, typeError: true })
            }
            maxSize={5}
            onSizeError={() =>
              setUploadError({ sizeError: true, typeError: false })
            }
          />
        )}
        {uploadError.sizeError && (
          <p className="profile-image__uploader-validation">
            File must not exceed 5Mb.
          </p>
        )}
        {uploadError.typeError && (
          <p className="profile-image__uploader-validation">
            Only PNG, JPG or JPEG allowed.
          </p>
        )}

        {!!avatar && (
          <>
            <ReactCrop
              aspect={ASPECT_CROP}
              crop={crop}
              onChange={(c) => setCrop(c)}
              circularCrop
              keepSelection
            >
              <img src={avatar} ref={imgRef} onLoad={onImageLoad} />
            </ReactCrop>

            <canvas ref={canvasRef} style={{ display: "none" }} />
          </>
        )}
      </div>
      <div className="profile-image__footer">
        <div className="profile-image__buttons-wrapper">
          <button onClick={closeModal} className="my-profile__submit-button">
            Cancel
          </button>
          <button
            onClick={uploadHandler}
            className="my-profile__submit-button my-profile__submit-button--dark"
            disabled={isDisabled}
          >
            Upload
          </button>
        </div>
      </div>
    </>
  );
};
